import { useCallback, useMemo, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { TableProps } from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';
import type { FormTemplate } from '../../models/TaskForm/TaskForm';
import type {
  TaskFormTemplatesRequest,
  selectTaskFormManagementTemplatesStatus,
} from '../../store/reducers/TaskFormSlice';
import { useTableHeight } from '../../hooks/components/useTableHeight';
import { TableFooter } from '../TableFooter/TableFooter';
import { LoadingTable } from '../shared/Loading';
import { TableResults } from '../shared/TableResults';
import tableDefaults from '../shared/antTableDefaults.module.css';
import {
  getQuestionnaireTemplateColumns,
  SORT_KEY_BY_COLUMN,
} from './questionnaireTemplateColumns';
import type {
  TemplateTableActions,
  TemplateTableRow,
} from './questionnaireTemplateColumns';
import styles from './questionnaireTemplatesList.module.css';

export const DEFAULT_TABLE_REQUEST: Required<
  Pick<TaskFormTemplatesRequest, 'PageIndex' | 'PageSize'>
> &
  Pick<
    TaskFormTemplatesRequest,
    'Filter.TextSearch' | 'SortKeySelector' | 'SortOrder'
  > = {
  PageIndex: 0,
  PageSize: 20,
  SortKeySelector: 'name',
  SortOrder: 'Asc',
  'Filter.TextSearch': '',
};

interface Props {
  readonly templates: readonly FormTemplate[];
  readonly totalCount: number;
  readonly status: ReturnType<typeof selectTaskFormManagementTemplatesStatus>;
  readonly userNicknamesById: Readonly<Record<string, string>>;
  readonly request: TaskFormTemplatesRequest;
  readonly onRequestChange: Dispatch<SetStateAction<TaskFormTemplatesRequest>>;
  readonly actions: TemplateTableActions;
}

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  const date = dayjs(dateStr);
  return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : '-';
};

export const QuestionnaireTemplatesTable = ({
  templates,
  totalCount,
  status,
  userNicknamesById,
  request,
  onRequestChange,
  actions,
}: Props) => {
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const rows = useMemo<TemplateTableRow[]>(
    () =>
      templates.map((template) => ({
        key: template.id,
        id: template.id,
        name: template.name ?? 'Без названия',
        questions: template.questions?.length ?? 0,
        createdAt: formatDate(template.createdDateTime),
        user: template.createdUserId
          ? (userNicknamesById[template.createdUserId] ?? '')
          : '',
      })),
    [templates, userNicknamesById],
  );
  const { tableHeight, containerHeight, isTabletScroll } = useTableHeight({
    tableContainerRef,
    items: rows,
  });
  const tableScroll = isTabletScroll ? { y: tableHeight } : undefined;
  const handleTableChange = useCallback<
    NonNullable<TableProps<TemplateTableRow>['onChange']>
  >(
    (_pagination, _filters, sorter) => {
      if (status.responseLoading) return;
      const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const field = activeSorter?.order ? activeSorter.field : undefined;
      const sortKeySelector =
        field === 'name' || field === 'questions' || field === 'createdAt'
          ? SORT_KEY_BY_COLUMN[field]
          : DEFAULT_TABLE_REQUEST.SortKeySelector;
      onRequestChange((current) => ({
        ...current,
        PageIndex: 0,
        SortKeySelector: sortKeySelector,
        SortOrder:
          activeSorter?.order === 'descend'
            ? 'Desc'
            : DEFAULT_TABLE_REQUEST.SortOrder,
      }));
    },
    [status.responseLoading, onRequestChange],
  );
  const columns = getQuestionnaireTemplateColumns({
    request,
    templates,
    loading: status.responseLoading,
    actions,
  });

  return (
    <div
      ref={tableContainerRef}
      className={cn(styles.tableWrap, tableDefaults.defaultTableContainer)}
    >
      <TableResults
        isEmpty={rows.length === 0 && totalCount === 0 && !status.responseError}
        loading={status.responseLoading}
      >
        <div style={{ height: `${containerHeight}px` }}>
          <LoadingTable<TemplateTableRow>
            rowKey="id"
            columns={columns}
            dataSource={rows}
            pagination={false}
            showSorterTooltip={false}
            loading={status.responseLoading}
            locale={{
              emptyText: status.responseError
                ? status.responseError
                : 'Шаблоны анкет не найдены',
            }}
            className={cn(styles.table, tableDefaults.defaultTable)}
            scroll={tableScroll}
            onChange={handleTableChange}
            data-testid="questionnaire-templates-table"
          />
        </div>
        <div
          style={{
            height: '1px',
            backgroundColor: 'rgb(218, 204, 204)',
            margin: '0px',
          }}
        />
        <TableFooter
          pageIndex={request.PageIndex ?? DEFAULT_TABLE_REQUEST.PageIndex}
          pageSize={request.PageSize ?? DEFAULT_TABLE_REQUEST.PageSize}
          total={totalCount}
          onPageChange={(page, size) => {
            onRequestChange((current) => ({
              ...current,
              PageIndex: page - 1,
              PageSize: size,
            }));
          }}
          onPageSizeChange={(value) => {
            onRequestChange((current) => ({
              ...current,
              PageSize: Number(value),
              PageIndex: 0,
            }));
          }}
          pageSizeOptions={['10', '20', '50', '100']}
          disabled={status.responseLoading}
        />
      </TableResults>
    </div>
  );
};
